import { apiPath } from '../../constants';
import {
    axios,
    apiRequestOptions,
    transformHttpResponse,
} from 'utils';
import { fetchMsacFinalResults } from './listAction';

/**
 * The judge's manual order for a shooting-score tie inside ONE rule
 * category (CategoryShootingTieBreak — category-specific, never stored on
 * the shooting result: an athlete may compete in several categories).
 *
 *  - no stored decision + order   → POST (create)
 *  - stored decision + new order  → PATCH (update)
 *  - stored decision + null order → DELETE (the athletes are tied again)
 *
 * `section` — `shooting` (default) or `bout` (a bout place the bracket left
 * shared, 14 Oct 2026) — is sent on create only; a decision never changes
 * section.
 *
 * Every write re-fetches the final standings: the backend is the only
 * place places/points are calculated, so the page always displays the
 * re-ranked result, never a local guess.
 *
 * The promise settles when the WRITE does (27 Oct 2026) — the screen marks
 * the row ✓ «the server has it» at once — and the standings re-read behind
 * it. A create refused because the decision already exists (a second move
 * made before the standings brought its id back) updates that one instead.
 */
// A judges' order was written — the category's overview (whether it may be
// confirmed, and why not) is out of date (overviewReducer).
export const MSAC_TIE_BREAK_SAVE_SUCCESS = 'MSAC_TIE_BREAK_SAVE_SUCCESS';

export const saveMsacTieBreak = ({ competitionId, categoryId, sportsmanId, tieBreakId, manualOrder, section = 'shooting', refresh = true }) => {
    return function(dispatch) {
        let options;
        if (null === manualOrder) {
            if (!tieBreakId) {
                return Promise.resolve(); // nothing stored, nothing to clear
            }
            options = apiRequestOptions(apiPath.CATEGORY_SHOOTING_TIE_BREAKS.DELETE, null, {
                params: { id: tieBreakId },
            });
        } else if (tieBreakId) {
            options = apiRequestOptions(apiPath.CATEGORY_SHOOTING_TIE_BREAKS.PATCH, { manualOrder }, {
                params: { id: tieBreakId },
            });
        } else {
            options = apiRequestOptions(apiPath.CATEGORY_SHOOTING_TIE_BREAKS.POST, {
                competition: '/api/competitions/' + competitionId,
                ruleCategory: '/api/rule_categories/' + categoryId,
                sportsman: '/api/sportsmen/' + sportsmanId,
                manualOrder,
                section,
            });
        }

        const creating = !tieBreakId && null !== manualOrder;
        const updateExisting = error => {
            if (!creating || !error.response || 422 !== error.response.status) {
                return Promise.reject(error);
            }

            return axios(apiRequestOptions(apiPath.CATEGORY_SHOOTING_TIE_BREAKS.LIST, {
                competition: competitionId, ruleCategory: categoryId, sportsman: sportsmanId, section,
            }))
                .then(transformHttpResponse)
                .then(response => {
                    const list = Array.isArray(response.data) ? response.data : (response.data && response.data['hydra:member']) || [];
                    if (0 === list.length) {
                        return Promise.reject(error);
                    }

                    return axios(apiRequestOptions(apiPath.CATEGORY_SHOOTING_TIE_BREAKS.PATCH, { manualOrder }, {
                        params: { id: list[0].id },
                    })).then(transformHttpResponse);
                });
        };

        return axios(options)
            .then(transformHttpResponse)
            .catch(updateExisting)
            .then(() => {
                dispatch({ type: MSAC_TIE_BREAK_SAVE_SUCCESS, competition: competitionId, category: categoryId });
                // Behind the write, not before the answer. A caller saving
                // several rows at once (`refresh: false`) re-reads ONCE, after
                // the last: a read started between two writes can put the
                // in-between standings back into the cache after the second
                // write cleared it (27 Oct 2026, «УВАГА» that would not go).
                if (refresh) {
                    dispatch(fetchMsacFinalResults(competitionId));
                }
            })
            .catch(error => {
                const data = error.response && error.response.data;
                const violations = data && data.violations;
                const message = (violations && violations.length && violations[0].message)
                    || (data && data['hydra:description'])
                    || (data && data.message)
                    || 'Save failed';
                return Promise.reject(new Error(message));
            });
    }
};
